// @givmo/app-admin — the match-approval CHARGE dialog (07-1.2). ★ MONEY-MOVING ★
//
// Ports the legacy Flutter `MatchPopUp` / `EtfMatchPopUp` confirm-then-charge flow onto the SaaS
// dialog. Approving a match CHARGES A REAL CARD — or withdraws from the COMPANY WALLET — via
// `POST /admin/company/matches/{id}/approve` (or the grouped twin) — the SAME live route the
// production Flutter webapp uses — so this component is the place every money-safety invariant is
// enforced:
//
//   1. IDEMPOTENCY — a stable `Idempotency-Key` is minted ONCE when the user confirms the charge (a
//      `useRef`-held `IdempotencyKeyHolder`, stable across re-renders) and REUSED for any retry of the
//      SAME action. The backend `claim_idempotency` dependency de-dupes only across same-key retries;
//      an unkeyed retry double-charges — so the key is load-bearing, never regenerated on retry. The
//      series even SURVIVES a close/reopen of the dialog: the holder keys by the (match-set, card)
//      signature and is NOT cleared on the open-transition, so re-entering the SAME approval after a
//      maybe-charged close stays in the same series (the backend replays, never re-charges), while a
//      different approval mints fresh by construction. The holder still clears on confirmed success and
//      on a fatal (definite no-charge) error.
//      FROZEN LEGS (CAM-R59b). Beside the key the dialog holds the series it was minted for: the
//      price, the disclosure bodies and the amount the approval was confirmed at, and every leg it
//      POSTed, exactly as sent, with the key it went out under — held by the leg's match set, so
//      the same matches reached through either button (a row's Approve, or "Approve all" over just
//      that row) find the leg the other one sent. Charging again in the same series — "Try again",
//      or the same matches re-entered after a maybe-charged close — skips a leg already confirmed
//      and resends a leg in doubt VERBATIM under its own key, with no budget pre-check (once it
//      committed, its own spend counts against the budget, so a re-check would misreport it as over
//      budget), no re-pricing and the echo it carried (a registry that moved since would put a
//      different body under the held key, which the backend can only refuse — cut 7 review F2).
//      Only legs never attempted check the budget and price themselves, each at its own unit's
//      price, found by its match set, and only once every leg in doubt has been resent, so a new
//      leg's refusal never ends a series while a leg may have charged. While a series is held for
//      the approval on screen, the dialog states what it will resend: the disclosure, and for a
//      single or grouped request the amount and price of the leg already sent for it (an
//      Approve-all leg went out at its budget-capped amount), else the series' own. A reopen of the
//      same matches restores the series' funding source (the "Pay with" choice — the default card,
//      another saved card or the wallet) and its cover-fees choice, so the re-entered approval IS
//      the held series; only a source that is no longer offered falls back to the default card, a
//      new charge, so unticked and stated at its figures now. Through either button (the same one,
//      or the other: a row's Approve, or "Approve all" over just that row) only a series that sent a
//      leg is re-entered: one that sent none holds nothing in doubt, so that approval is a new
//      charge at its figures now, read on open like any other (EP-WEB46). No message after a leg
//      confirmed ever says nothing was charged.
//   2. NO BLIND RETRY of a MAYBE-CHARGED error — a 408/429/5xx/transport failure is classified
//      RETRYABLE (`isRetryableChargeError`): the charge MAY have gone through, so we surface an
//      explicit "check status / try again" state and re-POST under the SAME key only on the user's
//      say-so. A fatal 4xx (decline / over-budget / validation) rolls back cleanly with no retry.
//      DISMISSAL of the maybe-charged state is itself gated: Esc / overlay-click / Cancel do not close
//      silently — the user must explicitly acknowledge that the charge may have gone through. And once
//      a charge is attempted the funding-card field LOCKS, so no divergent series can start mid-charge.
//   3. SINGLE-FLIGHT — the Confirm button is disabled + shows a spinner while a charge is in flight;
//      a double-click cannot fire a second POST (an in-flight guard AND the disabled button), and it
//      cannot mint a second key (the holder returns the same key for the same action).
//   4. CONFIRM-BEFORE-CHARGE — the dialog states the amount + card + charity before any charge.
//   5. FUNDING REQUIRED — the approve needs a resolved funding source: a saved `card_id`, OR the
//      company wallet (`payment_method: 'Wallet'`, no card). With NO card AND no wallet that can cover
//      the match, the charge is inert and the dialog links to Settings → Payment Methods. The wallet
//      is NEVER an auto-default (a card, when present, stays the default) — it is only ever charged by
//      an explicit selection, and its option is DISABLED when the balance can't cover what it would
//      debit: the match, or once priced on the wallet the gross the dialog states, which covering the
//      fees raises (CAM-R80; a new series ticked past the balance holds Approve). We never POST an
//      approve without a resolved funding source.
//   6. BUDGET RESOLUTION — a match is charged at `min(requested, remaining match budget)`, NOT at the
//      raw request. The list shows that cap (EP-BE19's figure, D-EP-56: the admin sees the match,
//      never the employee's gift), but a list goes stale in EITHER direction: an earlier waiting
//      request decided or withdrawn elsewhere frees its share for the later ones (D-EP-56's in-order
//      rule). So on open the dialog reads what approving the request matches NOW (EP-WEB41,
//      `current-figures.ts`: `/pending`'s `approvable`, bounded, never blocking) and re-checks THAT
//      through `allowed_amount` — asked a figure, the route can only confirm it or answer less, so
//      asked a stale one it would book the lower match. A read that fails, is slow or does not list
//      the request leaves the listed figure, as before. The confirm copy + the Confirm label state the
//      real number before the user commits (invariant 4 would otherwise be a lie), and a number other
//      than the list's is stated as a budget that moved since the list loaded. A shortfall discovered
//      at charge time is a genuine race (a concurrent admin) and stops the charge for
//      re-confirmation; the backend's `COMPANY_BUDGET_EXCEEDED` 400 remains the authority and is
//      handled as a fatal (chargeless) error. Approve-all reads the same figures on open and charges
//      each unit at its figure now, oldest request first (the server's figures claim the budgets in
//      that order, so they add up) — a request no longer waiting drops out, one left out on the list
//      joins once something is left for it (`currentApproveAll`) — re-checking each leg's cap inside
//      the charge loop (the checks do not reserve budget, so N up-front cap calls would each see the
//      same headroom). Only that budget code reads as a spent budget on open: the cap routes also
//      refuse a request that is no longer pending (CAM-R34 — the approve routes' own 400/404), and a
//      failed read is neither. A series re-entered after a maybe-charged close (invariant 1) is not
//      re-read: it resends what it was confirmed with (with its source gone, or with no leg sent,
//      the reopen is a new charge, read like any other).
//   7. DISCLOSURE-BEFORE-CHARGE (CAM-X4 web) — paying a match is the COMPANY giving, so the dialog
//      reads the company-payer body for each unit it will charge, on the selected rail
//      (`match-disclosure.ts`: the backend derives the target, so a collection match — whose id the
//      admin wire withholds — is covered), shows it under the "Terms of your donation" divider
//      directly above the confirm control (D-CAM-41: the dialog leads with the match summary; the
//      full text sits in a bounded region below it, never collapsed), and
//      echoes each unit's body (`shown_disclaimer_*`) on that unit's approve request. Approve-all
//      shows each distinct body once, labelled with the recipients it applies to. Confirm stays
//      disarmed until every body has resolved; a failed read says so and offers a retry — no match
//      is paid without its text. A funding change re-reads (the rail picks the body: a card payment
//      is a contribution with a receipt, a wallet payment a grant with none). A `409
//      DISCLOSURE_STALE` is a definite no-charge for that unit (the gate runs before the charge).
//   8. THE SERVER'S PRICE (CAM-BE13, D-CAM-40) — a match payment may cover its fees, as a company
//      deposit may (the "Cover processing fees" row, off on every open). Every figure the row and the
//      charge line state comes from the server's quote for this dialog's units on the selected rail
//      (`components/charge-quote.ts`: each unit priced as its own charge by the charge's own
//      resolution — the stored card's brand row, the company's own setting, its negotiated platform
//      rate). A single or grouped request is priced at its budget-capped amount; Approve all at each
//      unit's requested amount, its totals stated as an upper bound. Each approve request carries the
//      choice plus the gross and fee stated for its unit (`expected_amount_charged` /
//      `expected_fee`), and a backend that would charge other figures refuses with `409
//      CHARGE_QUOTE_CHANGED` before money moves. An Approve-all leg the budget caps below its request
//      is re-priced at its cap before it charges, and never above the figure stated for it. Confirm is
//      held while the quote loads or has failed. The choice is part of the charge series, whose
//      frozen legs carry the price they were stated with (invariant 1).
//
// The dialog is DRIVEN by the page: the page computes the `ApproveTarget` (a single row, a grouped
// row's complete batch, or the Approve-all group set) and the funding-card list, and owns the side
// effects via two callbacks — `onApproved` (confirmed success: invalidate the dashboard query + toast)
// and `onCloseAfterAttempt` (the dialog closed after ANY charge attempt WITHOUT a confirmed success —
// a fatal error or an acknowledged maybe-charged close — so the page refreshes the approvals + wallet
// reads the user is told to check). This component owns the charge state machine + the money-safety guards.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import {
  sdk,
  centsToMoneyString,
  firstErrorCode,
  isGivmoError,
  supportReference,
  GivmoError,
} from '@givmo/api-client';
import { Button, Dialog, Icon, Select, testIdProp, type SelectOption } from '@givmo/design-system';
import { isDisclosureStaleError, type ResolvedDisclosure } from '@givmo/disclosure';
import { CreditCard, AlertTriangle } from 'lucide-react';
import {
  ChargeDisclosureTerms,
  DisclosureUnavailable,
  useChargeDisclosure,
} from '../../components/charge-disclosure';
import {
  CHARGE_QUOTE_CHANGED,
  breakdownFor,
  fetchMatchQuote,
  matchQuoteQueryKey,
  statedCharge,
  toMatchQuote,
  type ChargeQuote,
  type MatchQuote,
} from '../../components/charge-quote';
import { ChargeSummary, type ChargeSummaryItem } from '../../components/charge-summary';
import { CharitiesToggle, CollectionCharities } from './collection-charities';
import {
  chargeQuoteFigures,
  coverFeesRowCopy,
  hasNothingToCover,
} from '../../components/cover-fees-copy';
import { CoverFeesRow } from '../../components/cover-fees-row';
import { useRevealOnShow } from '../../components/reveal-on-show';
import { useEmployerModulePaths } from '../../lib/module-paths';
import { cardLabel, type PaymentCard } from '../settings/payment-methods-decode';
import { formatChargeFigure, formatFeeFigure } from '../settings/wallet-decode';
import {
  currentApproveAll,
  fetchCurrentFigures,
  matchSetKey,
  type CurrentApproveAll,
} from './current-figures';
import {
  buildApproveGroupedBody,
  buildApproveMatchBody,
  buildApproveWalletGroupedBody,
  buildApproveWalletMatchBody,
  chargeableCents,
  charityCountLabel,
  collectionLocked,
  collectionPhrase,
  formatApprovalUsd,
  groupKindName,
  parseCentsAmount,
  totalApprovableAmount,
  walletCoversAmount,
  walletOptionLabel,
  type ApprovalCharge,
  type ApprovalGroup,
  type CollectionLabel,
  type GroupedMember,
  type WalletFundingOption,
} from './dashboard-decode';
import { isRetryableChargeError, maybeChargedLine } from '@givmo/payments';
import {
  createIdempotencyKeyHolder,
  approvalFingerprint,
  approvalWireFingerprint,
  type ApprovalFunding,
} from './idempotency';
import {
  disclosureForMatches,
  fetchMatchDisclosures,
  matchDisclosureSections,
  type MatchDisclosureLeg,
} from './match-disclosure';

const PREFIX = 'dashboard-approve';

/**
 * The "Pay with" option value that means "fund from the company wallet" (as opposed to a saved card
 * id). A namespaced sentinel that cannot collide with a real Shift4 card id.
 */
const WALLET_OPTION_VALUE = '__company_wallet__';

/**
 * Apply an `allowed_amount` response as a cap on `requested`.
 *
 * BOTH budget routes are cent-precise. `GET /matches/{id}/allowed_amount` always returned `float`;
 * `POST /matches/grouped/allowed_amount` was annotated `-> int` until backend `7ab6d1e9`, so FastAPI
 * TRUNCATED the service's cent-quantized Decimal on the way out and a $16.66 ETF with ample budget
 * read back as "allowed 16".
 *
 * While the grouped wire truncated, this helper deliberately ignored a cap it could not prove: the
 * true cap lay in `[allowed, allowed + 1)`, so only `allowed + 1 <= requested` established one
 * regardless of the lost cents. That compensation is now WRONG rather than merely redundant — a
 * genuine cap within a dollar of the request (allowed $16.00 against a requested $16.66) would fail
 * that test, charge the full request, and be hard-rejected by the backend's budget guard instead of
 * partially approving. So the value is taken at face value on both routes.
 *
 * An unreadable value is never a cap: charge the request and let the backend's own guard be the
 * authority — it either accepts, or 400s chargeless with a clear message. Never over-charge, never
 * silently under-charge.
 */
export function applyAllowedCap(requested: number, allowed: unknown): number {
  if (typeof allowed !== 'number' || !Number.isFinite(allowed)) return requested;
  return allowed < requested ? allowed : requested;
}

/**
 * What the dialog is charging: one pending row's single match, one grouped (ETF/fundraiser) row's
 * complete batch, or the Approve-all group set. The 'grouped' target is the Approve-all machinery
 * scoped to exactly one grouped unit — same grouped routes, same per-leg key derivation.
 */
export type ApproveTarget =
  | {
      kind: 'single';
      /** The `{match_id}` to approve. */
      matchId: string;
      /** The amount the list showed, in dollars (cents allowed; > 0): what approving this request
       *  matched when the list loaded (EP-BE19's figure, the server's own cap — never the employee's
       *  gift, D-EP-56). The dialog reads the figure now and re-checks it — see §6. */
      amount: number;
      charityName: string;
      /** The charity's logo for the summary's mark (initials when absent). */
      logoUrl?: string | null;
      /** The employee whose gift this matches, as the summary's context line words it ("Matching
       *  {…}’s gift"): their name, or "an anonymous employee" (D-CAM-68, `employeeInSentence`). */
      employeeName?: string;
    }
  | {
      kind: 'grouped';
      /** EVERY leg's match id — the request's identity here, and the complete pending set the
       *  grouped routes require. */
      matchIds: string[];
      /** The amount the list showed for the whole request, in dollars (cents allowed; > 0): what
       *  approving it matched when the list loaded, read again and re-checked on open — see §6. */
      amount: number;
      /** The group's display name for the confirm copy (ETF/fundraiser name or a count fallback). */
      groupName: string;
      charityCount: number;
      /** The wire's group kind ('ETF' / 'Fundraiser'), for the summary's context line, or null. */
      kindLabel?: string | null;
      /** The employee whose gift this matches, as the summary's context line words it ("Matching
       *  {…}’s gift"): their name, or "an anonymous employee" (D-CAM-68, `employeeInSentence`). */
      employeeName?: string;
      /** A charity-collection gift's collection (EP-WEB40): the summary names it in its words and
       *  opens its charities ("{N} charities ›"); null or absent for any other grouped request. */
      collection?: CollectionLabel | null;
      /** The request's charities, in its order: what "{N} charities ›" opens. */
      members?: readonly GroupedMember[];
    }
  | {
      kind: 'all';
      /** Approve all's units (singles + grouped units) in request order, oldest first, each at
       *  its row's figure (EP-BE19: the waiting requests' figures already claim the budgets in
       *  that order, so they add up). */
      groups: ApprovalGroup[];
      /** The units' total — an UPPER BOUND: a leg whose budget moved since the list loaded is
       *  capped again as it charges. */
      amount: number;
      /** Waiting requests left out: nothing is left for them. */
      leftOut?: number;
      /**
       * Every waiting request the list showed, oldest first: the units, and the ones it left out
       * (`amountValue` null). The dialog re-prices them at their figures now (§6, EP-WEB41), so one
       * left out on the list joins once something is left for it. Absent, it re-prices `groups`.
       */
      requests?: ApprovalGroup[];
    };

/**
 * How many units Approve all lists; a longer batch shows one fewer and a "+ N more" line. Four on a
 * wide screen. On a phone, three — two matches, then the line — so the dialog fits a 390 × 844
 * screen with its terms and Approve all in view now that it carries the cover-fees row (the short
 * list, as ruled: D-CAM-62).
 */
export function approveAllListMax(phone: boolean): number {
  return phone ? 3 : 4;
}

/** A phone-width viewport: below the design system's `sm` breakpoint (40rem). */
const PHONE_MEDIA_QUERY = '(max-width: 39.9375rem)';

/** Whether the viewport is phone-width, following resizes. */
function usePhoneWidth(): boolean {
  const [phone, setPhone] = useState(
    () =>
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia(PHONE_MEDIA_QUERY).matches,
  );
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const query = window.matchMedia(PHONE_MEDIA_QUERY);
    const onChange = () => setPhone(query.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);
  return phone;
}

/**
 * Approve all's compact list (D-CAM-41): each approvable unit as its recipient (the charity's logo,
 * or initials — a grouped row's wire carries no logo), who asked, and the amount its pending row
 * shows. The total above it is the batch's upper bound; each leg is capped as it charges.
 */
function approveAllList(
  groups: readonly ApprovalGroup[],
  max: number,
): {
  items: ChargeSummaryItem[];
  overflow: string | null;
} {
  const listed = groups.length > max ? groups.slice(0, max - 1) : groups;
  const items = listed.map((group, index) => {
    const name = group.name ?? 'Match request';
    const subtitle = [
      groupKindName(group.kindLabel),
      group.employeeName ? `Matching ${group.employeeName}` : null,
    ]
      .filter((part): part is string => part !== null)
      .join(' · ');
    return {
      key: group.matchIds.join(','),
      mark: { name, logoUrl: group.logoUrl ?? null, locked: group.markLocked === true },
      title: name,
      subtitle: subtitle.length > 0 ? subtitle : null,
      amount: group.amountLabel ?? formatApprovalUsd(group.amountValue),
      testId: `${PREFIX}-summary-item-${index}`,
    };
  });
  const hidden = groups.length - listed.length;
  return {
    items,
    overflow: hidden > 0 ? `+ ${hidden} more ${hidden === 1 ? 'match' : 'matches'}` : null,
  };
}

export interface ApproveMatchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Null while closed; the charge target once opened. */
  target: ApproveTarget | null;
  /**
   * The company's vaulted cards (07-1.3). Empty AND no usable wallet → the inert "add a card" state.
   */
  cards: PaymentCard[];
  /**
   * The company wallet as a funding option (decoded from `GET /admin/company/wallet`), or null/omitted
   * when there is no readable wallet balance — in which case the dialog stays card-only exactly as
   * before (this prop is purely ADDITIVE; the card path is byte-identical when it is absent). When
   * present, a "Company wallet — $X.XX available" option is offered, DISABLED when the spendable
   * balance can't cover what the approval would debit (the match, or once priced on the wallet the
   * gross the dialog states). It is never an auto-default — a card, when present, stays selected.
   */
  walletFunding?: WalletFundingOption | null;
  /** The company's name — the match payment's donor of record (D-CAM-41); null while unknown. */
  companyName?: string | null;
  /** Fired ONCE after every group in the target has charged successfully. */
  onApproved: () => void;
  /**
   * Fired when the dialog closes after ANY charge attempt in this open-session WITHOUT a confirmed
   * success (a fatal error close, or an acknowledged maybe-charged close) — a confirmed success closes
   * through `onApproved` instead. The page refreshes the approvals + wallet reads so the pending list /
   * balance the user is told to check is fresh truth, not a stale cache (a maybe-charged approve may
   * have actually landed). A plain no-attempt Cancel fires neither callback.
   */
  onCloseAfterAttempt: () => void;
}

/** The price and the cover-fees choice a series was confirmed with (CAM-BE13). */
interface ApprovalPricing {
  readonly quote: MatchQuote;
  readonly covered: boolean;
}

/**
 * One approve request exactly as sent (CAM-R59b): its body, the Idempotency-Key it went out under,
 * the unit price it was stated with, and whether a 2xx has confirmed it.
 */
type FrozenLeg = (
  | {
      readonly op: 'single';
      readonly matchId: string;
      readonly body:
        | ReturnType<typeof buildApproveMatchBody>
        | ReturnType<typeof buildApproveWalletMatchBody>;
    }
  | {
      readonly op: 'grouped';
      readonly body:
        | ReturnType<typeof buildApproveGroupedBody>
        | ReturnType<typeof buildApproveWalletGroupedBody>;
    }
) & {
  /** The Idempotency-Key the leg was POSTed under; a resend goes out under it again. */
  readonly key: string;
  /** The price its figures were stated from: its unit's, or its cap's when the budget capped it. */
  readonly price: ChargeQuote;
  confirmed: boolean;
};

/**
 * An approval's charge series (CAM-R59b, invariant 1): what its key was minted for. Held beside the
 * key — across a close/reopen too — and dropped with it.
 */
interface ApprovalSeries {
  /** `approvalFingerprint` of the match set + funding source, without the cover-fees choice. */
  readonly base: string;
  /**
   * The sorted match ids, to restore the funding source and the cover-fees choice when the same
   * approval reopens.
   */
  readonly matchSet: string;
  /** The "Pay with" value the series was confirmed with (a card id, or the wallet sentinel). */
  readonly fundingValue: string;
  /**
   * The row's tick when the series began. With `base` it is the key's signature, so a held series
   * and its key are found by the same identity: the same approval with the same tick.
   */
  readonly coverChoice: boolean;
  readonly pricing: ApprovalPricing;
  /**
   * Each unit's price, by its match set (`matchSetKey`): a leg never attempted takes its own unit's
   * price, whatever order the units re-enter in (cut 13 fold review O2).
   */
  readonly unitPrices: ReadonlyMap<string, ChargeQuote>;
  /** The bodies the series was stated under: each leg's echo, and what the dialog shows for it. */
  readonly disclosureLegs: readonly MatchDisclosureLeg[];
  /** The amount it was confirmed at: a single or grouped request's capped amount. */
  readonly amount: number;
  /**
   * Every leg POSTed in this series, by its unit's match set (`matchSetKey`), not by its key: a row's
   * Approve and "Approve all" over that one row's matches (one match, or one grouped post) reach the
   * same series, derive different keys for its leg, and must both find the leg already sent.
   */
  readonly legs: Map<string, FrozenLeg>;
}

/** The charge state machine. `retryable-error` REUSES the idempotency key AND the series. */
type Phase =
  | { status: 'idle' }
  | { status: 'checking-budget' }
  | { status: 'charging' }
  | { status: 'retryable-error'; message: string; reference: string | null }
  | { status: 'fatal-error'; message: string; reference: string | null };

/** The match ids an approval charges (the idempotency signature's set). */
function targetMatchIds(target: ApproveTarget): string[] {
  if (target.kind === 'single') return [target.matchId];
  if (target.kind === 'grouped') return target.matchIds;
  return target.groups.flatMap((g) => g.matchIds);
}

/**
 * Whether a held series can be the approval on screen (invariant 1): one that has sent a leg, which
 * is then resent under its own key whichever button re-enters it. A series that sent no leg holds
 * nothing in doubt, so the approval on screen is a new charge at its figures now, through either
 * button: through the other it was stated from that button's figures (cut 13 fold review O4), and
 * through the same one the figure may have moved since it was stated (EP-WEB46; the steward read
 * of cut 61, N-3).
 */
function resumable(held: ApprovalSeries): boolean {
  return held.legs.size > 0;
}

/**
 * Whether a held series' funding source is still offered: its card is still saved, or the wallet
 * can still be read. When it is not, a reopen falls back to the default card, a new charge, which
 * is stated at its own figures like every new charge (cut 13 fold review O3; cut 61 review F-3).
 */
function sourceOffered(
  held: ApprovalSeries,
  cards: readonly PaymentCard[],
  walletFunding: WalletFundingOption | null,
): boolean {
  return held.fundingValue === WALLET_OPTION_VALUE
    ? walletFunding !== null
    : cards.some((card) => card.id === held.fundingValue);
}

const RETRYABLE_HINT =
  'We could not confirm whether the charge went through. Do not assume it failed. Check your ' +
  'donations, or try again — a retry is de-duplicated and will not double-charge.';

// No "nothing has been charged" in the two notes below: the read re-runs on a reopen and on a funding
// change, which can follow an approval that may have gone through (the CAM-R59 class of copy).
const DISCLOSURE_UNAVAILABLE_BODY =
  'We couldn’t load the disclosure for this match, so it can’t be approved yet.';

const DISCLOSURE_UNAVAILABLE_ALL_BODY =
  'We couldn’t load the disclosures for these matches, so they can’t be approved yet.';

const DISCLOSURE_STALE_BODY =
  'The disclosure for this match was just updated. Read the updated text below, then approve ' +
  'again. Nothing was charged.';

const CLOSE_ACK_BODY =
  'The charge may have gone through even though we could not confirm it. After closing, check your ' +
  'recent donations before approving again — re-approving the SAME match(es) with the same card and ' +
  'processing-fee choice is safe (it will not double-charge), but a different approval would be a ' +
  'new charge.';

// RULED (D-EP-56, amended 2026-10-16; EP-WEB41): Approve all's change line, and its words when
// nothing is left for any request it would take. The change line names the requests, not the
// budget: a request decided or withdrawn elsewhere moves the total with no budget changing.
const allFiguresChanged = (total: string) =>
  `Some of these requests changed since this list loaded, so these matches now total up to ${total}.`;
const ALL_NOTHING_LEFT =
  'Your remaining match budget is used up, so there is nothing left to approve right now. ' +
  'Nothing will be charged.';

const QUOTE_LOADING_CARD = 'Checking the processing fee for this card…';
const QUOTE_LOADING_WALLET = 'Checking the fees for this payment…';

const QUOTE_FAILED =
  'We could not check the processing fee for this match, so it cannot be approved right now. ' +
  'Close and try again. Nothing will be charged.';

const QUOTE_FAILED_ALL =
  'We could not check the processing fees for these matches, so they cannot be approved right ' +
  'now. Close and try again. Nothing will be charged.';

// A refused price names both halves: unticked, the charge is the match itself, so most refusals
// are the fees alone moving (`expected_fee`), never the total (cut 13 review F3).
const QUOTE_CHANGED =
  'The price of this match changed — its fees or its total — so nothing was charged. Check the ' +
  'new figures, then approve again.';

const QUOTE_CHANGED_ALL =
  'The price of these matches changed — their fees or their total — so nothing was charged. ' +
  'Check the new figures, then approve again.';

/** An amount in dollars as the quote's wire decimal ("16.66"), at the cents the charge sends. */
function quoteAmount(amount: number): string {
  return centsToMoneyString(chargeableCents(amount)) ?? '0.00';
}

export function ApproveMatchDialog({
  open,
  onOpenChange,
  target,
  cards,
  walletFunding = null,
  companyName = null,
  onApproved,
  onCloseAfterAttempt,
}: ApproveMatchDialogProps) {
  const navigate = useNavigate();
  const paths = useEmployerModulePaths();

  // ONE idempotency holder for this dialog's lifetime — a `useRef` so it is STABLE across re-renders
  // (a re-render must never mint a new key mid-charge → a double charge). Keyed by match-set + card.
  const idempotency = useRef(createIdempotencyKeyHolder());
  // The series the held key was minted for (CAM-R59b, invariant 1) — kept and dropped with the key.
  const series = useRef<ApprovalSeries | null>(null);

  // The default funding CARD (server-marked default, else the first card). This — never the wallet —
  // is what seeds the selection, so a company with a card keeps that card as the default and the
  // wallet stays an explicit opt-in choice.
  const defaultCardId = useMemo(() => {
    const preferred = cards.find((c) => c.isDefault) ?? cards[0];
    return preferred?.id ?? null;
  }, [cards]);
  // The selected "Pay with" option VALUE: a card id, the wallet sentinel (WALLET_OPTION_VALUE), or
  // null when nothing is selected yet. Defaults to the default card id (never the wallet).
  const [fundingValue, setFundingValue] = useState<string | null>(defaultCardId);
  const [phase, setPhase] = useState<Phase>({ status: 'idle' });
  // CAM-BE13 (D-CAM-40): the per-approval "Cover processing fees" choice — off on every open.
  const [coverFees, setCoverFees] = useState(false);
  const queryClient = useQueryClient();
  const phone = usePhoneWidth();
  // The maybe-charged close acknowledgement step (invariant 2): armed when the user tries to dismiss a
  // `retryable-error` state; only its explicit confirm button actually closes.
  const [closeAck, setCloseAck] = useState(false);
  // It replaces the footer, so on a phone it opens below the fold: it is brought into view (P5).
  const closeAckRef = useRevealOnShow<HTMLDivElement>(
    phase.status === 'retryable-error' && closeAck,
  );
  // A collection gift's charities, opened from the summary (EP-WEB40); closed on every open. Their
  // shares are read no earlier than this open, when the dialog's own figure is read (cut 31's F1).
  const [charitiesOpen, setCharitiesOpen] = useState(false);
  const [openedAt, setOpenedAt] = useState(0);

  // Guard against a double-submit racing the React state update: a synchronous ref flips true the
  // instant a charge starts and back to false when it settles, so a second click during the same tick
  // is a no-op even before `phase` re-renders the disabled button.
  const inFlight = useRef(false);
  // Whether ANY charge attempt fired in this open-session — drives `onCloseAfterAttempt` on close.
  const attempted = useRef(false);

  // Reset ONLY on a genuine CLOSED→OPEN transition: re-seed the card, clear the phase + the ack step +
  // the attempt flag. ★ It MUST NOT re-run merely because `defaultCardId` changed while already open —
  // a cards-list refetch changing the default id mid-charge would otherwise release the flight lock and
  // let a subsequent 408 "Try again" mint a FRESH key → the backend can't de-dupe → a double charge.
  // So we gate on a real open-transition via `prevOpen`, and never touch `inFlight` on a `defaultCardId`
  // change.
  //
  // ★ The idempotency holder is DELIBERATELY NOT cleared here (EP-FIX2). It keys by the (match-set,
  // card) signature, so persisting it across close/reopen keeps a maybe-charged approval's series
  // alive: reopening the SAME approval reuses the SAME key (the backend replays — no double charge),
  // while a different match-set/card mints fresh by construction. Clearing on reopen was the latent
  // escape hatch that made close→reopen→same-approval a FRESH key the backend could not dedupe. The
  // holder still clears on confirmed success and on a fatal (definite no-charge) error.
  const prevOpen = useRef(false);
  useEffect(() => {
    if (open && !prevOpen.current) {
      // The same approval, re-entered with a series held (a maybe-charged close), comes back AS that
      // series: its funding source and its cover-fees choice, so "Approve" resends the leg in doubt
      // under its key and the backend replays or completes it (CAM-R59b). Re-selecting the source by
      // hand would start a new series (the "Pay with" change below drops the held one), so the dialog
      // restores it itself. A source no longer offered — the card was removed, the wallet cannot be
      // read — falls back to the default card: a new charge, so unticked like every new charge
      // (D-CAM-37; cut 13 fold review O3). Anything else starts at the default card, unticked.
      const held = series.current;
      const reentered =
        held !== null &&
        target !== null &&
        held.matchSet === matchSetKey(targetMatchIds(target)) &&
        resumable(held)
          ? held
          : null;
      const heldSourceOffered =
        reentered !== null && sourceOffered(reentered, cards, walletFunding);
      setFundingValue(
        reentered !== null && heldSourceOffered ? reentered.fundingValue : defaultCardId,
      );
      setPhase({ status: 'idle' });
      setCoverFees(reentered !== null && heldSourceOffered ? reentered.coverChoice : false);
      setCloseAck(false);
      setCharitiesOpen(false);
      setOpenedAt(Date.now());
      attempted.current = false;
      inFlight.current = false;
    }
    prevOpen.current = open;
    // NB: `defaultCardId` (and `cards`, `walletFunding`, `target`) are intentionally NOT in the dep
    // array — see the note above (a reset must never fire on a card-default or props change, only on
    // an open-transition). They are read only inside the open-transition branch, where the effect
    // closes over THIS render's values (correct on the flip).
    // (This repo's ESLint has no react-hooks/exhaustive-deps rule, so no disable directive is needed.)
  }, [open]);

  // Auto-select the default CARD once it becomes available (e.g. the cards query resolves AFTER the
  // dialog opened — the open-transition reset above ran while `defaultCardId` was still null, leaving
  // the inert no-card state even though the company has cards). Only ever selects a card (the wallet
  // is never auto-selected — `defaultCardId` is a card id or null). KEY-SAFE: this ONLY
  // `setFundingValue` — it never touches the idempotency key or the flight lock, so the Fix-2
  // invariant (a cards change must not drop the key mid-charge) still holds. Guarded to fire ONLY when
  // nothing is selected yet AND no charge is in flight, so it never overrides a user selection or an
  // in-flight charge (once `fundingValue` is non-null the guard is false → it settles in one render).
  useEffect(() => {
    if (open && fundingValue === null && defaultCardId !== null && !inFlight.current) {
      setFundingValue(defaultCardId);
    }
  }, [open, fundingValue, defaultCardId]);

  const busy = phase.status === 'checking-budget' || phase.status === 'charging';
  // Once a charge has been attempted (in flight OR maybe-charged), the funding field LOCKS: a new
  // (match-set, funding) series while a prior approve may have gone through is exactly the
  // double-charge vector. It unlocks only in `idle`/`fatal-error` (definite no-charge → safe to
  // re-scope).
  const locked = busy || phase.status === 'retryable-error';

  // ── BUDGET RESOLUTION (the amount actually charged) ──────────────────────────────────────────────
  //
  // What the company OWES on a match request is the employee's gift capped by the remaining match
  // budget (`min(requested, year_budget_per_employee − approved_YTD, annual limit − approved_YTD)`):
  // a $200/employee policy against a $1,000 gift is a $200 match — the NORMAL case. The list already
  // carries that cap (EP-BE19), so the target's `amount` IS the match, and the admin never sees the
  // gift (D-EP-56). The backend hard-REJECTS an approve above the cap (`_enforce_budgets` raises; it
  // never silently caps), so the client must send the capped number.
  //
  // We resolve it HERE, on open, so confirm-before-charge stays honest: the dialog opens on the
  // listed match and states the real number BEFORE the user commits. The list's figure can be stale
  // either way (an earlier request decided or withdrawn elsewhere frees its share for this one), and
  // `allowed_amount` answers what it is asked or less, so asked the listed figure it would confirm a
  // stale LOWER match. So the dialog first reads what approving the request matches NOW (EP-WEB41,
  // `fetchCurrentFigures`) and asks the cap with that; a read that fails, is slow or does not list
  // the request asks with the listed figure, as before. A number other than the list's is stated
  // (`figureChanged`). (The shipped Flutter `MatchPopUp` seeded its amount from the cap too —
  // `_onReady`'s `allowedMatchAmount`.)
  //
  // Approve-ALL reads the same figures (`allResolved` below) but makes no cap call on open: the checks
  // are read-only and do not reserve budget, so N pre-flight calls would each see the same headroom
  // and over-count. Its units take what approving them in order matches now; each leg's check runs
  // inside the charge loop, AFTER the previous leg actually committed.
  const listedAmount = target?.amount ?? 0;
  const [resolved, setResolved] = useState<
    | { status: 'pending' }
    | { status: 'ready'; amount: number }
    | { status: 'exhausted' }
    | { status: 'unavailable'; reason: 'not-pending' | 'unchecked' }
  >({ status: 'pending' });

  useEffect(() => {
    if (!open || target === null || target.kind === 'all') {
      setResolved({ status: 'pending' });
      return;
    }
    let cancelled = false;
    setResolved({ status: 'pending' });
    const requestKey = matchSetKey(targetMatchIds(target));
    // A series held for this request resends what it was confirmed with (invariant 1): no new figure.
    // Its source gone, or no leg sent, the reopen is a new charge, read like any other.
    const held = series.current;
    const reentered =
      held !== null &&
      held.matchSet === requestKey &&
      resumable(held) &&
      sourceOffered(held, cards, walletFunding);
    void (async () => {
      // What approving the request matches now; no answer for it leaves the listed figure.
      const figures = reentered ? null : await fetchCurrentFigures();
      if (cancelled) return;
      const now = figures?.get(requestKey) ?? null;
      if (now?.kind === 'used-up') {
        setResolved({ status: 'exhausted' });
        return;
      }
      const asked = now?.kind === 'available' ? now.cents / 100 : target.amount;
      try {
        const { data: allowed } =
          target.kind === 'single'
            ? await sdk.donationMatchesCheckCompanyMatchAllowedAmount({
                path: { match_id: target.matchId },
                query: { amount: asked },
                throwOnError: true,
              })
            : await sdk.donationMatchesCheckGroupedMatchesAllowedAmount({
                body: { match_ids: target.matchIds, amount: asked },
                throwOnError: true,
              });
        if (cancelled) return;
        const capped = applyAllowedCap(asked, allowed);
        setResolved(capped > 0 ? { status: 'ready', amount: capped } : { status: 'exhausted' });
      } catch (caught: unknown) {
        // The budget routes RAISE (400 COMPANY_BUDGET_EXCEEDED) rather than returning 0 when there is
        // no headroom left, so THAT code reads as exhausted. Any other failure must not be dressed up
        // as a spent budget: a request decided or cancelled since the list loaded is refused with the
        // approve routes' own 400/404 (CAM-R34), and anything else is a read that failed. Either way
        // the Confirm control stays inert and the copy says why — we never fall back to charging the
        // uncapped request.
        if (cancelled) return;
        const err = isGivmoError(caught) ? caught : null;
        if (err !== null && firstErrorCode(err) === 'COMPANY_BUDGET_EXCEEDED') {
          setResolved({ status: 'exhausted' });
        } else if (err !== null && (err.status === 400 || err.status === 404)) {
          setResolved({ status: 'unavailable', reason: 'not-pending' });
        } else {
          setResolved({ status: 'unavailable', reason: 'unchecked' });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, target]);

  // Approve all's units at what approving them matches NOW (EP-WEB41): one read on open, and Confirm
  // waits for it. A read that fails or is slow leaves the list's units, and a series re-entered after
  // a maybe-charged close is not re-read: it resends what it was confirmed with (invariant 1) — unless
  // its source is gone or it sent no leg, when the reopen is a new charge.
  const [allResolved, setAllResolved] = useState<CurrentApproveAll | null>(null);
  useEffect(() => {
    if (!open || target === null || target.kind !== 'all') {
      setAllResolved(null);
      return;
    }
    let cancelled = false;
    setAllResolved(null);
    const held = series.current;
    const reentered =
      held !== null &&
      held.matchSet === matchSetKey(targetMatchIds(target)) &&
      resumable(held) &&
      sourceOffered(held, cards, walletFunding);
    void (async () => {
      const figures = reentered ? null : await fetchCurrentFigures();
      if (!cancelled) setAllResolved(currentApproveAll(target, figures));
    })();
    return () => {
      cancelled = true;
    };
  }, [open, target]);

  /** Approve all's units as the dialog states and charges them: at their figures now once read. */
  const allGroups: readonly ApprovalGroup[] | null =
    target?.kind === 'all' ? (allResolved?.groups ?? target.groups) : null;

  // What the Confirm button will actually charge. Approve-all states its units' total as an upper
  // bound (each leg's cap is checked again as it charges); everything else opens on the listed match
  // and waits for the re-checked number. A spent budget charges nothing, so it shows $0.
  const amount =
    allGroups !== null
      ? (totalApprovableAmount([...allGroups]) ?? 0)
      : resolved.status === 'ready'
        ? resolved.amount
        : resolved.status === 'exhausted'
          ? 0
          : listedAmount;

  /** Every target must have its figures before Confirm: Approve all's units, or the capped amount. */
  const amountResolved =
    target?.kind === 'all' ? allResolved !== null : resolved.status === 'ready';
  const resolving =
    target !== null &&
    (target.kind === 'all' ? allResolved === null : resolved.status === 'pending');

  /** The figure moved since the list loaded, either way: approving now matches another number. */
  const figureChanged =
    target !== null &&
    target.kind !== 'all' &&
    resolved.status === 'ready' &&
    chargeableCents(resolved.amount) !== chargeableCents(listedAmount);
  /** Approve all's units or their figures moved since the list loaded. */
  const allChanged = allResolved?.changed ?? false;

  // The resolved funding source, derived PURELY from the SELECTION state (`fundingValue`) — never from
  // `cards`/`walletFunding`. This is deliberate + money-safety-critical: a props refetch (cards or
  // wallet) MID-CHARGE must NEVER re-scope or drop the funding, or a maybe-charged retry would be
  // stranded / re-keyed → a double charge (the same invariant the raw-`cardId` code held). Selectability
  // is gated at the OPTION level instead — the wallet option is only offered when a wallet exists and is
  // DISABLED when it can't cover, so `fundingValue` only ever holds a validly-selected value. This is the
  // SINGLE source of truth for what gets charged, and threads the idempotency signature + the wire body.
  const funding = useMemo<ApprovalFunding | null>(() => {
    if (fundingValue === null) return null;
    return fundingValue === WALLET_OPTION_VALUE
      ? { kind: 'wallet' }
      : { kind: 'card', cardId: fundingValue };
  }, [fundingValue]);

  // The set of match ids being charged — the idempotency signature (with the card). Order-independent.
  const matchIds = useMemo<string[]>(
    () =>
      allGroups !== null
        ? allGroups.flatMap((g) => g.matchIds)
        : target === null
          ? []
          : targetMatchIds(target),
    [target, allGroups],
  );

  // ── The donor disclosure (invariant 7) ───────────────────────────────────────────────────────────
  // The units this dialog charges, exactly as the charge loop walks them (it skips a unit with no
  // chargeable total or no match ids, so none is read for one here either), each with the name its
  // body is labelled with when Approve all shows several.
  const disclosureUnits = useMemo<{ matchIds: string[]; name: string | null }[]>(() => {
    if (target === null) return [];
    if (target.kind === 'single') return [{ matchIds: [target.matchId], name: target.charityName }];
    if (target.kind === 'grouped') return [{ matchIds: target.matchIds, name: target.groupName }];
    // Approve all's units are known once their figures are read (nothing is read for a stale unit).
    if (allResolved === null) return [];
    return allResolved.groups
      .filter((g) => g.amountValue !== null && g.matchIds.length > 0)
      .map((g) => ({ matchIds: g.matchIds, name: g.name ?? null }));
  }, [target, allResolved]);
  // The rail picks the body; nothing is read until a funding source is selected.
  const rail = funding === null ? null : funding.kind === 'wallet' ? 'wallet' : 'card';
  const disclosureKey =
    open && rail !== null && disclosureUnits.length > 0
      ? `${rail}|${disclosureUnits.map((unit) => unit.matchIds.join(',')).join(';')}`
      : null;
  const { state: disclosureState, reload: reloadDisclosure } = useChargeDisclosure(
    disclosureKey,
    () => (rail === null ? Promise.resolve(null) : fetchMatchDisclosures(disclosureUnits, rail)),
  );
  const disclosureLegs = disclosureState.status === 'ready' ? disclosureState.value : null;

  // ── The server's price (invariant 8) ───────────────────────────────────────────────────────────
  // One unit per charge the loop makes, in its order: a single or grouped request at its
  // budget-capped amount (so it waits for the cap), Approve all at each unit's own figure — what
  // approving them all in request order matches for it (EP-BE19), never the employee's gift.
  // Nothing is priced until a funding source is selected (the rail and card set the price).
  const quoteAmounts = useMemo<string[] | null>(() => {
    if (target === null || funding === null || !amountResolved) return null;
    if (target.kind !== 'all') return [quoteAmount(amount)];
    return (allGroups ?? [])
      .filter((g) => g.amountValue !== null && g.matchIds.length > 0)
      .map((g) => quoteAmount(g.amountValue ?? 0));
  }, [target, funding, amountResolved, amount, allGroups]);
  const quoteQuery = useQuery({
    queryKey: matchQuoteQueryKey(quoteAmounts ?? [], funding ?? { kind: 'wallet' }),
    queryFn: () => fetchMatchQuote(quoteAmounts ?? [], funding ?? { kind: 'wallet' }),
    enabled: open && quoteAmounts !== null && quoteAmounts.length > 0 && funding !== null,
    select: (body: unknown) => toMatchQuote(body, quoteAmounts?.length ?? 0),
    staleTime: 0,
  });
  const quoteStatus: 'none' | 'loading' | 'failed' | 'ready' =
    quoteAmounts === null || quoteAmounts.length === 0
      ? 'none'
      : quoteQuery.isPending
        ? 'loading'
        : quoteQuery.isError || quoteQuery.data == null
          ? 'failed'
          : 'ready';
  const liveQuote = quoteStatus === 'ready' ? (quoteQuery.data ?? null) : null;

  // The series held for the approval on screen (invariant 1): in flight, in doubt, or re-entered
  // after a maybe-charged close with the same funding source and choice. While one is held, the dialog
  // states what it will resend — its price, disclosure and amount; otherwise the live reads.
  const heldSeries = series.current;
  const pinned =
    heldSeries !== null &&
    target !== null &&
    funding !== null &&
    heldSeries.base === approvalFingerprint({ matchIds, funding }) &&
    heldSeries.coverChoice === coverFees &&
    resumable(heldSeries)
      ? heldSeries
      : null;
  // The leg the held series already sent for the single or grouped request on screen, whichever
  // button sent it: "Approve" resends that leg, so the dialog states the leg's own amount and price.
  // (A series "Approve all" minted over this one request holds the request's amount and its price
  // there, while its leg went out at the budget-capped amount.)
  const heldLeg =
    pinned !== null && target !== null && target.kind !== 'all'
      ? (pinned.legs.get(matchSetKey(matchIds)) ?? null)
      : null;
  const pendingPricing = pinned?.pricing ?? null;
  const shownQuote: MatchQuote | null =
    heldLeg !== null
      ? { ...heldLeg.price, units: [heldLeg.price] }
      : (pendingPricing?.quote ?? liveQuote);
  const shownDisclosureLegs = pinned?.disclosureLegs ?? disclosureLegs;
  const quoteFigures = shownQuote === null ? null : chargeQuoteFigures(shownQuote);
  const companyCoversFees = shownQuote?.companyCoversFees ?? false;
  // Nothing to cover (the wallet rail with no platform fee): unticked, locked with the reason.
  const nothingToCover = hasNothingToCover(quoteFigures);
  const covered = pendingPricing?.covered ?? (companyCoversFees || (coverFees && !nothingToCover));
  const breakdown = shownQuote === null ? null : breakdownFor(shownQuote, covered);
  const upTo = target?.kind === 'all';
  const rowCopy = coverFeesRowCopy({
    figures: quoteFigures,
    upTo,
    rail: funding?.kind === 'wallet' ? 'wallet' : 'card',
  });
  const coverFeesLocked = locked || shownQuote === null || companyCoversFees || nothingToCover;

  // Whether the wallet (when present) can cover what THIS approval would debit from it, mirroring the
  // backend's `gross_amount > available` gate: on the wallet rail once priced, the gross the dialog
  // states, which covering the fees raises above the match (CAM-R80); before that, the match itself,
  // the uncovered debit. Drives the option's disabled state (an insufficient wallet can never be
  // selected, and one ticked past its balance holds Approve) and gates the wallet as a funding SOURCE
  // for the inert check.
  const walletDebitCents =
    funding?.kind === 'wallet' && breakdown !== null
      ? parseCentsAmount(breakdown.amountCharged)
      : null;
  const walletCovers =
    walletFunding !== null &&
    (walletDebitCents !== null
      ? walletFunding.availableCents >= walletDebitCents
      : walletCoversAmount(walletFunding, amount));

  // The "Pay with" options: every saved card, then the wallet (when a wallet balance is readable). The
  // wallet option is DISABLED when it can't cover what this approval would debit — Base UI won't let a
  // disabled option be selected, so an insufficient wallet can never become the funding source
  // client-side.
  const fundingOptions: SelectOption[] = useMemo(() => {
    const opts: SelectOption[] = cards.map((c) => ({ value: c.id, label: cardLabel(c) }));
    if (walletFunding !== null) {
      opts.push({
        value: WALLET_OPTION_VALUE,
        label: walletOptionLabel(walletFunding, walletCovers),
        disabled: !walletCovers,
      });
    }
    return opts;
  }, [cards, walletFunding, walletCovers]);

  // A usable funding SOURCE exists to offer: a saved card, or a wallet that can cover this match. The
  // dialog shows the funding selector when there IS such a source OR a funding is already selected (the
  // latter keeps the charge UI mounted even if a props refetch mid-charge would otherwise flip the
  // source away — never yank the confirm/retry UI out from under an in-flight approval). With neither,
  // it shows the inert add-a-card prompt. A funding SELECTION (`funding !== null`) separately enables
  // Confirm.
  const hasFundingSource = cards.length > 0 || walletCovers;
  const canFund = hasFundingSource || funding !== null;
  // A new series never debits the wallet past its balance; a held one is resent as it was, since its
  // attempt in doubt may already have debited it (invariant 1).
  const walletShort = pinned === null && funding?.kind === 'wallet' && !walletCovers;

  const runCharge = useCallback(async () => {
    if (target === null) return;
    if (funding === null) return; // inert without a funding source — never charge.
    if (inFlight.current) return; // single-flight: a second click is a no-op.

    // The units the loop charges, in its order (it skips a unit with no chargeable total or no match
    // ids, so none is disclosed or priced for one either).
    const units: readonly (readonly string[])[] =
      target.kind === 'single'
        ? [[target.matchId]]
        : target.kind === 'grouped'
          ? [target.matchIds]
          : (allGroups ?? [])
              .filter((g) => g.amountValue !== null && g.matchIds.length > 0)
              .map((g) => g.matchIds);

    // The series this charge belongs to (invariant 1): the one held for this approval — "Try
    // again", or the same approval re-entered after a close — else a new one, stated from what the
    // dialog shows now.
    const base = approvalFingerprint({ matchIds, funding });
    const held = series.current;
    let active: ApprovalSeries;
    if (held !== null && held.base === base && held.coverChoice === coverFees && resumable(held)) {
      active = held;
    } else {
      // Never pay a match without its disclosure and its price shown (invariants 7, 8) — the
      // control is disarmed too. Every unit's body is checked before the first charge, so a batch
      // can never pay its early units and then find one without a body. Nor debit the wallet past
      // its balance (CAM-R80).
      if (disclosureLegs === null || liveQuote === null || walletShort) return;
      if (units.some((ids) => disclosureForMatches(disclosureLegs, ids) === null)) return;
      if (liveQuote.units.length !== units.length) return;
      active = {
        base,
        matchSet: matchSetKey(matchIds),
        fundingValue: funding.kind === 'wallet' ? WALLET_OPTION_VALUE : funding.cardId,
        coverChoice: coverFees,
        pricing: { quote: liveQuote, covered },
        // The quote priced exactly these units, in this order.
        unitPrices: new Map(units.map((ids, index) => [matchSetKey(ids), liveQuote.units[index]!])),
        disclosureLegs,
        amount,
        legs: new Map(),
      };
    }
    const { pricing } = active;

    inFlight.current = true;
    attempted.current = true; // a charge routine ran → a close now refreshes the reads to check.

    // Mint (or REUSE, on a retry of this same action) the stable key BEFORE any request. The key is
    // scoped to the exact match set + funding source + cover-fees choice; a same-action retry gets
    // the SAME value. For a new single-match leg this IS the leg's key. For Approve-all it is a
    // retry-stable SEED — each new leg derives its OWN key from it + the leg's identity (see below),
    // because the backend `claim_idempotency` dedup identity is (Idempotency-Key,
    // endpoint_class=METHOD:route_TEMPLATE, principal, body-fingerprint): sharing ONE key across legs
    // would make same-body legs REPLAY each other (silent under-charge) and different-body legs 409
    // (partial charge) on the FIRST run — not just retries. A leg already sent keeps the key it went
    // out under, whichever button sent it. The series is bound to the key here and leaves with it.
    const key = idempotency.current.keyFor({ matchIds, funding, coverFees: active.coverChoice });
    series.current = active;
    const dropSeries = () => {
      idempotency.current.clear();
      series.current = null;
    };

    // The wire body for one leg, routed by the resolved funding source. CARD → the existing
    // `payment_method: 'Credit Card'` + card_id body (byte-identical to the card-only path); WALLET →
    // `payment_method: 'Wallet'` + `card_id: null` (the backend's in-process wallet withdraw). The
    // amount is identical either way — only the funding source differs. Each carries the echo of the
    // body its unit was shown, and the cover-fees choice with the gross + fee stated for its unit
    // (invariant 8). Built only for a leg never attempted; a leg once sent is resent as it was.
    const shownFor = (ids: readonly string[]): ResolvedDisclosure | null =>
      disclosureForMatches(active.disclosureLegs, ids);
    const chargeFor = (unit: ChargeQuote): ApprovalCharge => ({
      coverFees: pricing.covered,
      stated: statedCharge(breakdownFor(unit, pricing.covered)),
    });
    const singleLeg = (
      amt: number,
      matchId: string,
      price: ChargeQuote,
      legKey: string,
    ): FrozenLeg => ({
      op: 'single',
      matchId,
      body:
        funding.kind === 'card'
          ? buildApproveMatchBody({
              amount: amt,
              cardId: funding.cardId,
              disclosure: shownFor([matchId]),
              charge: chargeFor(price),
            })
          : buildApproveWalletMatchBody({
              amount: amt,
              disclosure: shownFor([matchId]),
              charge: chargeFor(price),
            }),
      key: legKey,
      price,
      confirmed: false,
    });
    const groupedLeg = (
      ids: string[],
      amt: number,
      price: ChargeQuote,
      legKey: string,
    ): FrozenLeg => ({
      op: 'grouped',
      body:
        funding.kind === 'card'
          ? buildApproveGroupedBody({
              matchIds: ids,
              amount: amt,
              cardId: funding.cardId,
              disclosure: shownFor(ids),
              charge: chargeFor(price),
            })
          : buildApproveWalletGroupedBody({
              matchIds: ids,
              amount: amt,
              disclosure: shownFor(ids),
              charge: chargeFor(price),
            }),
      key: legKey,
      price,
      confirmed: false,
    });
    // POST a leg under the key it was frozen with, exactly as frozen, and mark it confirmed on a 2xx.
    const send = async (leg: FrozenLeg) => {
      setPhase({ status: 'charging' });
      const headers = { 'Idempotency-Key': leg.key };
      if (leg.op === 'single') {
        await sdk.donationMatchesApproveCompanyMatch({
          path: { match_id: leg.matchId },
          body: leg.body,
          headers,
          throwOnError: true,
        });
      } else {
        await sdk.donationMatchesApproveGroupedCompanyMatches({
          body: leg.body,
          headers,
          throwOnError: true,
        });
      }
      leg.confirmed = true;
    };

    // The over-budget block: the pre-check cap `allowed` is below the leg `want` → block with copy,
    // charge nothing further. The backend approve ALSO enforces budget (the real guard); this is the
    // fail-fast nicety, so it must be ROUTED CORRECTLY (a single match has no post_id, so it CANNOT
    // use the grouped budget route — that 400s → falsely fatal → the primary Approve never charges).
    // RACE GUARD. The cap was already resolved on open and is what the user confirmed, so reaching
    // here means the remaining budget MOVED between confirm and charge (a concurrent approval by
    // another admin). Charging the newly-lower number would charge something the user never
    // confirmed, so we stop, say the budget changed, and let them re-confirm the new amount. Only a
    // leg never attempted is pre-checked, so nothing was charged in this series: it goes, and the
    // re-confirm starts afresh at the new cap.
    const blockBudgetChanged = (allowed: number): boolean => {
      dropSeries();
      inFlight.current = false;
      setResolved(allowed > 0 ? { status: 'ready', amount: allowed } : { status: 'exhausted' });
      setPhase({
        status: 'fatal-error',
        message:
          allowed > 0
            ? `Your remaining match budget changed while you were approving — the most you can ` +
              `approve now is ${formatApprovalUsd(allowed)}. Nothing was charged. Close and ` +
              `re-approve to confirm the new amount.`
            : `Your remaining match budget is used up. Nothing was charged.`,
        reference: null,
      });
      return true;
    };

    // Approve-all ran the budget dry part-way through. The legs that DID charge are real money that
    // already moved — say so precisely rather than reporting a flat failure, and drop the key (those
    // legs are settled and this one was never sent; a re-approve of the remainder is a genuinely new
    // series). `charged` counts every leg this series confirmed, in earlier attempts too.
    const endBatchOutOfBudget = (charged: number, total: number): boolean => {
      dropSeries();
      inFlight.current = false;
      setPhase({
        status: 'fatal-error',
        message:
          charged === 0
            ? 'Your remaining match budget is used up, so nothing was approved and nothing was charged.'
            : `Your remaining match budget ran out after ${charged} of ${total} ` +
              `${total === 1 ? 'match' : 'matches'}. ` +
              `${charged === 1 ? 'That one has' : `Those ${charged} have`} been approved and ` +
              `charged; the rest are still pending.`,
        reference: null,
      });
      return true;
    };

    // Legs confirmed in this series so far (earlier attempts included), for the partial-completion
    // messages when a batch stops part-way (the budget runs out, or a later unit's disclosure or price
    // changed under it).
    let chargedLegs = 0;
    let totalLegs = 1;

    // A batch stopped before charging a unit whose price could not be stated: that unit was never
    // sent, the ones before it are confirmed, and no leg is left in doubt — so the series goes.
    const endBatchUnpriced = (reason: 'failed' | 'changed'): boolean => {
      dropSeries();
      inFlight.current = false;
      if (reason === 'changed')
        void queryClient.resetQueries({ queryKey: ['company-match-quote'] });
      setPhase({
        status: 'fatal-error',
        message:
          chargedLegs === 0
            ? reason === 'failed'
              ? QUOTE_FAILED_ALL
              : QUOTE_CHANGED_ALL
            : `${
                reason === 'failed'
                  ? 'We could not check the processing fee for one of these matches,'
                  : 'The price of one of these matches changed — its fees or its total —'
              } so it was not charged. ${chargedLegs} of the ${totalLegs} matches ` +
              `${chargedLegs === 1 ? 'was' : 'were'} approved and charged before it. Close and ` +
              `approve the rest again${reason === 'changed' ? ' to check the new figures' : ''}.`,
        reference: null,
      });
      return true;
    };

    // The price of an Approve-all leg the budget capped below its request: re-read at the cap, and
    // never above the figure stated for the unit (the batch's total is an upper bound).
    const cappedUnitPrice = async (
      unit: ChargeQuote,
      capped: number,
    ): Promise<ChargeQuote | 'failed' | 'changed'> => {
      let requoted: MatchQuote | null;
      try {
        requoted = toMatchQuote(await fetchMatchQuote([quoteAmount(capped)], funding), 1);
      } catch {
        return 'failed';
      }
      const price = requoted?.units[0];
      if (price === undefined) return 'failed';
      // Exact cents from the server's decimals, only to compare two stated figures.
      const capGross = parseCentsAmount(breakdownFor(price, pricing.covered).amountCharged);
      const statedGross = parseCentsAmount(breakdownFor(unit, pricing.covered).amountCharged);
      if (capGross === null || statedGross === null || capGross > statedGross) return 'changed';
      return price;
    };

    try {
      if (target.kind === 'single') {
        // ── SINGLE match. A leg this series already sent for it — from this Approve, or from
        //    "Approve all" over this one match — is resent verbatim under the key it went out
        //    under, with no pre-check; otherwise re-check the cap on the DEDICATED single-match
        //    route (a standalone match has post_id=None → the grouped budget route 400s), then
        //    charge the RESOLVED amount — the budget-capped number the user just confirmed, not the
        //    raw request — under the seed itself. ──
        const unitSet = matchSetKey([target.matchId]);
        const sent = active.legs.get(unitSet);
        if (sent === undefined) {
          setPhase({ status: 'checking-budget' });
          const { data: allowed } = await sdk.donationMatchesCheckCompanyMatchAllowedAmount({
            path: { match_id: target.matchId },
            query: { amount: active.amount },
            throwOnError: true,
          });
          const stillAllowed = applyAllowedCap(active.amount, allowed);
          if (stillAllowed < active.amount) return void blockBudgetChanged(stillAllowed);

          const leg = singleLeg(active.amount, target.matchId, pricing.quote.units[0]!, key);
          active.legs.set(unitSet, leg);
          await send(leg);
        } else if (!sent.confirmed) {
          await send(sent);
        }
        chargedLegs = 1;
      } else {
        // ── APPROVE-ALL / one GROUPED row: per leg, routed by leg type. A leg this series already
        //    confirmed is skipped; a leg in doubt is resent verbatim under the key it went out under
        //    (found by its unit's match set, so a leg a row's own Approve sent is found too); a leg
        //    never attempted is checked against the CURRENT remaining budget (after legs 1..N-1),
        //    priced, and charged. A leg over its cap blocks that leg (+ the rest) and charges
        //    nothing further (earlier legs already charged is inherent + fail-safe). A single leg
        //    uses the single-match budget+approve routes; a grouped leg uses the grouped routes. A
        //    new leg gets a DISTINCT-but-retry-stable key (seed + the leg's own fingerprint):
        //    distinct per leg ⇒ no cross-leg dedup ⇒ every leg charges on the first run;
        //    retry-stable (seed + each leg's matchIds unchanged on "Try again") ⇒ a leg in doubt
        //    re-POSTs under ITS key. A 'grouped' target (one grouped row's Approve) is EXACTLY this
        //    machinery scoped to its one grouped unit — same routes, same key derivation as that
        //    unit under Approve-all. ──
        const groups: ApprovalGroup[] =
          target.kind === 'all'
            ? [...(allGroups ?? [])]
            : [
                {
                  kind: 'grouped',
                  matchIds: target.matchIds,
                  // The RESOLVED (budget-capped) total the user confirmed — not the raw request.
                  amountValue: active.amount,
                },
              ];
        totalLegs = groups.length;
        // A leg in doubt is resent before any leg never attempted, whatever order the units
        // re-enter in, so a new leg's refusal can never end a series while a leg may have charged
        // (cut 61 review F-4). The rest keep their order.
        const inDoubt = (group: ApprovalGroup) =>
          active.legs.get(matchSetKey(group.matchIds))?.confirmed === false;
        const ordered = [...groups.filter(inDoubt), ...groups.filter((group) => !inDoubt(group))];
        for (const group of ordered) {
          // A group with no chargeable total (or no match ids) should have been filtered by the
          // caller; guard anyway so a malformed group never fires an unpriced/empty charge.
          const groupAmount = group.amountValue;
          if (groupAmount === null || group.matchIds.length === 0) continue;

          const unitSet = matchSetKey(group.matchIds);
          const sent = active.legs.get(unitSet);
          if (sent !== undefined) {
            if (!sent.confirmed) await send(sent);
            chargedLegs += 1;
            continue;
          }
          // The price stated for THIS unit, found by its match set: a series re-entered with its
          // units in another order still pairs each with its own (cut 13 fold review O2).
          const unit = active.unitPrices.get(unitSet);
          if (unit === undefined) return void endBatchUnpriced('failed');
          // A new leg's key: seed + a FIXED-LENGTH hash of the leg's identity
          // (`approvalWireFingerprint` — sorted match set + funding source). The
          // raw fingerprint grows with leg count and blew the backend's 255-char
          // header cap on real ETF groups (finding ④); the hash is
          // deterministic across retries, synchronous (no new suspension point
          // in the charge loop), and 69 chars total at any leg count.
          const legKey = `${key}:${approvalWireFingerprint({ matchIds: group.matchIds, funding })}`;

          setPhase({ status: 'checking-budget' });
          const { data: allowed } =
            group.kind === 'single'
              ? await sdk.donationMatchesCheckCompanyMatchAllowedAmount({
                  path: { match_id: group.matchIds[0]! },
                  query: { amount: groupAmount },
                  throwOnError: true,
                })
              : await sdk.donationMatchesCheckGroupedMatchesAllowedAmount({
                  body: { match_ids: group.matchIds, amount: groupAmount },
                  throwOnError: true,
                });

          // Under Approve-ALL the cap is resolved HERE (see the resolution note above — the checks
          // don't reserve budget, so this is the only place a leg's true headroom is known: after
          // legs 1..N-1 have actually committed). A leg over its cap charges the CAP; a leg with no
          // headroom left ENDS the batch, reporting what did land. A single `grouped` target has
          // already been resolved + confirmed, so a shortfall there is the race guard.
          const capped = applyAllowedCap(groupAmount, allowed);
          if (target.kind !== 'all' && capped < groupAmount) return void blockBudgetChanged(capped);
          if (capped <= 0) return void endBatchOutOfBudget(chargedLegs, groups.length);

          // The figures this leg states: its unit's, or — capped below its request — its price at
          // the cap, never above the unit's stated figure.
          const price =
            chargeableCents(capped) === chargeableCents(groupAmount)
              ? unit
              : await cappedUnitPrice(unit, capped);
          if (price === 'failed' || price === 'changed') return void endBatchUnpriced(price);

          const leg =
            group.kind === 'single'
              ? singleLeg(capped, group.matchIds[0]!, price, legKey)
              : groupedLeg(group.matchIds, capped, price, legKey);
          // Frozen BEFORE the POST: from here a retry resends exactly this.
          active.legs.set(unitSet, leg);
          await send(leg);
          chargedLegs += 1;
        }
      }

      // Confirmed: DROP the key and the series (a brand-new action gets a brand-new series), release
      // the flight lock, and hand success to the page (invalidate + toast + close).
      dropSeries();
      inFlight.current = false;
      setPhase({ status: 'idle' });
      onApproved();
    } catch (caught: unknown) {
      inFlight.current = false;
      const err = isGivmoError(caught)
        ? caught
        : new GivmoError({ message: 'Something went wrong. Please try again.', kind: 'unknown' });
      const reference = supportReference(err);
      // DISCLOSURE STALE (409): the gate refused this unit's echo BEFORE charging it — its text changed
      // while the dialog was open. Nothing moved for it, so drop the key. With nothing charged yet,
      // re-read and ask for a fresh confirm against the new text; part-way through Approve all, say
      // what did land and send the admin back through the refreshed list (the units already paid are
      // no longer pending, so re-running this batch could not complete it).
      if (isDisclosureStaleError(err)) {
        dropSeries();
        if (chargedLegs === 0) {
          setPhase({ status: 'fatal-error', message: DISCLOSURE_STALE_BODY, reference: null });
          reloadDisclosure();
        } else {
          setPhase({
            status: 'fatal-error',
            message:
              `The disclosure for one of these matches was just updated, so it was not charged. ` +
              `${chargedLegs} of the ${totalLegs} matches ${chargedLegs === 1 ? 'was' : 'were'} ` +
              `approved and charged before it. Close and approve the rest again to read the ` +
              `updated text.`,
            reference: null,
          });
        }
        return;
      }
      // RETRYABLE (maybe-charged): KEEP the key AND the series, so the next attempt re-POSTs the SAME
      // leg and the backend replays it. Surface the explicit check-status/try-again state — NEVER
      // auto-retry. Its line is the server's own sentence where the server worded one, never a
      // processor's message; a refusal that stopped this attempt while an earlier one settles says
      // that alone (EP-WEB34).
      if (isRetryableChargeError(err)) {
        setPhase({
          status: 'retryable-error',
          message: maybeChargedLine(err, RETRYABLE_HINT),
          reference,
        });
        return;
      }
      // FATAL (definite no-charge for the leg that got it; the ones before it are confirmed, so no
      // leg is in doubt): drop the key and the series so a fresh attempt is a genuinely new series,
      // and show the server copy. The consumer idempotency seam RELEASES a key on a 4xx rather than
      // caching it, so this is about a stale series, not a cached refusal. The optimistic-free UI
      // implies NO success.
      dropSeries();
      if (firstErrorCode(err) === CHARGE_QUOTE_CHANGED) {
        // The stated figures are no longer what this unit's charge would take (a card-brand fee
        // row, a re-rate, the company's own setting), refused before any money moved for it. Discard
        // the refused price — RESET, not invalidate, so no refused figure is stated while the new
        // one loads — and ask again; part-way through Approve all, say what did land.
        void queryClient.resetQueries({ queryKey: ['company-match-quote'] });
        setPhase({
          status: 'fatal-error',
          message:
            chargedLegs === 0
              ? target.kind === 'all'
                ? QUOTE_CHANGED_ALL
                : QUOTE_CHANGED
              : `The price of one of these matches changed — its fees or its total — so it ` +
                `was not charged. ${chargedLegs} of the ${totalLegs} matches ` +
                `${chargedLegs === 1 ? 'was' : 'were'} approved and charged before it. Close and ` +
                `approve the rest again to check the new figures.`,
          reference: null,
        });
        return;
      }
      setPhase({ status: 'fatal-error', message: err.message, reference });
    }
  }, [
    target,
    allGroups,
    funding,
    matchIds,
    amount,
    coverFees,
    covered,
    liveQuote,
    walletShort,
    queryClient,
    onApproved,
    disclosureLegs,
    reloadDisclosure,
  ]);

  // The ONLY close path. Fires `onCloseAfterAttempt` when a charge was attempted this open-session
  // (without a confirmed success — success closes via `onApproved`) so the page refreshes the
  // approvals + wallet reads the user is told to check — and after an open-time refusal (CAM-R34):
  // a request decided or cancelled since the list loaded would otherwise still show as waiting — or
  // a figure read on open that the list does not show (EP-WEB41): a moved figure, or nothing left
  // for the request (the read's `used-up`, or the cap's spent budget), so the list catches up.
  const stale =
    resolved.status === 'unavailable' ||
    resolved.status === 'exhausted' ||
    figureChanged ||
    allChanged;
  const finalizeClose = useCallback(() => {
    if (attempted.current || stale) onCloseAfterAttempt();
    onOpenChange(false);
  }, [onCloseAfterAttempt, onOpenChange, stale]);

  // Dismissal gate: never close mid-flight; a maybe-charged (`retryable-error`) dismissal ALWAYS routes
  // through the explicit acknowledgement step (Esc / overlay-click / Cancel arm it, only the ack button
  // actually closes); everything else closes normally.
  const requestClose = useCallback(() => {
    if (busy) return;
    if (phase.status === 'retryable-error') {
      setCloseAck(true);
      return;
    }
    finalizeClose();
  }, [busy, phase.status, finalizeClose]);

  // ── No-card inert state ──
  const goToPaymentMethods = () => {
    onOpenChange(false);
    // The Payment Methods TAB, not bare Settings: since the Match Program left Settings (EP-MNT2) the
    // first tab is Privacy, and a door named for Payment Methods lands on it. In the console the same
    // door is the Organization's Wallet (EP-MNT4's address book).
    void navigate({ to: paths.paymentMethods.to, search: paths.paymentMethods.search });
  };

  const title = target?.kind === 'all' ? 'Approve all pending matches?' : 'Approve this match?';
  const groupCount = allGroups !== null ? allGroups.length : 1;
  // Approve all, read on open, finds nothing left for any request it would take: it says so and offers
  // only Close (EP-WEB34, cut 25's review P2; D-EP-61). No figure, source, fee choice or charge control
  // stands over nothing to charge, and closing refreshes the list (`stale`: its units moved).
  const allNothingLeft =
    target?.kind === 'all' && pinned === null && allResolved !== null && groupCount === 0;

  // The dialog states EXACTLY what will happen before any charge — the summary carries the amount,
  // the recipient and the donor of record, the "Pay with" control the source, and the line below it
  // how the money moves ("this deducts from your company wallet immediately" vs "this charges your
  // card immediately"). The amount is the same either way — only the funding source differs. While
  // a series is held (invariant 1) it is the amount that series will resend: the amount of the leg
  // it already sent for this request, or the amount it was confirmed at.
  const pinnedAmount = heldLeg !== null ? heldLeg.body.amount : (pinned?.amount ?? amount);
  const shownAmount = pinned !== null && target?.kind !== 'all' ? pinnedAmount : amount;
  const amountLabel = formatApprovalUsd(shownAmount);
  const isWallet = funding?.kind === 'wallet';

  // Stated when the figure read on open is not what the list showed, either way — the admin must never
  // wonder why the number moved, and must never think a shortfall is an error they can retry away.
  // Never the employee's gift (D-EP-56). A held series resends what it was confirmed with, so it states
  // none, and a stopped charge's own message (a budget that moved at Confirm) says it instead.
  const noteChange = pinned === null && phase.status !== 'fatal-error';
  const changedNote =
    noteChange && figureChanged
      ? `Your match budget changed since this list loaded, so this match is now ${formatApprovalUsd(
          amount,
        )}.`
      : noteChange && allChanged && groupCount > 0
        ? allFiguresChanged(formatApprovalUsd(amount))
        : '';
  const body = ((): string => {
    if (target === null) return '';
    // A held series resends what it was confirmed with, whatever the cap reads now (its own spend
    // may count against the budget once it committed).
    const checkedOnOpen = pinned === null && target.kind !== 'all';
    if (checkedOnOpen && resolved.status === 'exhausted')
      return 'Your remaining match budget is used up, so there is nothing left to approve for this employee right now. Nothing will be charged.';
    if (checkedOnOpen && resolved.status === 'unavailable')
      return resolved.reason === 'not-pending'
        ? 'This match request is no longer pending — it may already have been approved, denied or cancelled. Nothing will be charged.'
        : 'We could not check your remaining match budget, so this match cannot be approved right now. Close and try again. Nothing will be charged.';
    if (checkedOnOpen && resolved.status === 'pending')
      return 'Checking your remaining match budget…';
    const allCheckedOnOpen = pinned === null && target.kind === 'all';
    if (allCheckedOnOpen && allResolved === null) return 'Checking your remaining match budget…';
    if (allNothingLeft) return ALL_NOTHING_LEFT;
    // The summary above states the amount, the recipient and the donor of record (D-CAM-41), and
    // the "Pay with" control names the source — so this line says only what happens, and why the
    // number may be below the request.
    if (funding === null) {
      // No funding source picked yet (reachable in the wallet-only case before the user selects, or for
      // a one-render tick before a card auto-selects) — a neutral prompt, NEVER an untrue "charge to
      // your card" when there is no card on file.
      return target.kind === 'all'
        ? 'Choose how to pay for these matches.'
        : 'Choose how to pay for this match.';
    }
    // The line states the server's gross (invariant 8; Approve all's is an upper bound). The fee is
    // the row's line above, so it is not restated, and ticking the row changes one figure without
    // reflowing the dialog. While there is no price it says why Confirm is held.
    const charged =
      breakdown === null
        ? null
        : `${upTo ? 'up to ' : ''}${
            breakdown.coverFees
              ? formatFeeFigure(breakdown.amountCharged)
              : formatChargeFigure(breakdown.amountCharged)
          }`;
    const consequence =
      charged !== null
        ? isWallet
          ? `This deducts ${charged} from your company wallet immediately.`
          : `This charges ${charged} to your card immediately.`
        : quoteStatus === 'failed'
          ? upTo
            ? QUOTE_FAILED_ALL
            : QUOTE_FAILED
          : isWallet
            ? QUOTE_LOADING_WALLET
            : QUOTE_LOADING_CARD;
    // A moved figure first, then the charge, as on a single request; then who is left out.
    if (target.kind === 'all') {
      const leftOut = allResolved?.leftOut ?? target.leftOut ?? 0;
      return [
        changedNote,
        consequence,
        leftOut > 0
          ? leftOut === 1
            ? '1 waiting request has no budget left, so it is not included.'
            : `${leftOut} waiting requests have no budget left, so they are not included.`
          : '',
      ]
        .filter((part) => part.length > 0)
        .join(' ');
    }
    return [changedNote, consequence].filter((part) => part.length > 0).join(' ');
  })();

  // The summary's figures (D-CAM-41): the amount this dialog will charge, who receives it and, for a
  // single or grouped request, whose gift it matches; Approve all lists its matches instead.
  const summaryQualifier = target?.kind === 'all' ? 'Up to' : null;
  // A charity-collection gift (EP-WEB40) is named in its words ("to the … collection", "to a Private
  // Charity Collection"), its mark is its cover or the lock, and its count is the control that opens
  // its charities.
  const collection = target?.kind === 'grouped' ? (target.collection ?? null) : null;
  const summaryRecipient =
    target === null || target.kind === 'all'
      ? null
      : target.kind === 'single'
        ? target.charityName
        : collection !== null
          ? collectionPhrase(collection)
          : target.groupName;
  const summaryCaption =
    target?.kind === 'all' && groupCount > 0
      ? `for ${groupCount} ${groupCount === 1 ? 'match' : 'matches'}`
      : null;
  const summaryContext: string[] = [];
  if (target?.kind === 'grouped' && collection === null) {
    const kind = groupKindName(target.kindLabel);
    const count = charityCountLabel(target.charityCount);
    summaryContext.push(kind !== null ? `${kind} · ${count}` : count);
  }
  if (target !== null && target.kind !== 'all' && target.employeeName) {
    summaryContext.push(`Matching ${target.employeeName}’s gift`);
  }
  const summaryMark =
    target === null || target.kind === 'all'
      ? null
      : target.kind === 'single'
        ? { name: target.charityName, logoUrl: target.logoUrl ?? null }
        : collection !== null
          ? {
              name: target.groupName,
              logoUrl: collection.coverUrl,
              locked: collectionLocked(collection),
            }
          : { name: target.groupName, logoUrl: null };
  // The collection's charities, each with its share where it adds up to the amount stated here.
  const charitiesId = `${PREFIX}-charities`;
  const charitiesToggle =
    target?.kind === 'grouped' && collection !== null ? (
      <CharitiesToggle
        count={target.charityCount}
        title={target.groupName}
        expanded={charitiesOpen}
        onToggle={() => setCharitiesOpen((wasOpen) => !wasOpen)}
        controls={charitiesId}
        testId={`${PREFIX}-charities-toggle`}
      />
    ) : null;
  const charitiesList =
    target?.kind === 'grouped' && collection !== null && charitiesOpen ? (
      <CollectionCharities
        id={charitiesId}
        request={{
          matchIds: target.matchIds,
          status: 'Pending',
          members: target.members ?? [],
          statedCents: chargeableCents(shownAmount),
        }}
        explainChange={false}
        since={openedAt}
        testId={charitiesId}
      />
    ) : null;
  const summaryList =
    target?.kind === 'all'
      ? approveAllList(allGroups ?? [], approveAllListMax(phone))
      : { items: [], overflow: null };

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        // Every dismiss vector (Esc, overlay-click, Cancel/Close) routes through the gate: an in-flight
        // charge never closes, and a maybe-charged state closes only via an explicit acknowledgement.
        if (!next) {
          requestClose();
          return;
        }
        onOpenChange(next);
      }}
    >
      <Dialog.Content
        aria-label={title}
        className="flex flex-col gap-16"
        width={480}
        testId={`${PREFIX}-dialog`}
      >
        <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
          {title}
        </Dialog.Title>

        {allNothingLeft ? (
          // ── Approve all with nothing left (D-EP-61): the sentence and one way out. ──
          <>
            <Dialog.Description
              className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary"
              {...testIdProp(`${PREFIX}-charge-line`)}
            >
              {ALL_NOTHING_LEFT}
            </Dialog.Description>
            <div className="mt-4 flex items-center justify-end gap-8">
              <Dialog.Close
                render={
                  <Button appearance="saas" tone="secondary" size="md" testId={`${PREFIX}-close`} />
                }
              >
                Close
              </Dialog.Close>
            </div>
          </>
        ) : !canFund ? (
          // ── Inert (no card on file AND no wallet that can cover this match, nothing selected): no
          //    charge control, just the add-a-card prompt (Settings → Payment Methods is also where a
          //    wallet is topped up). ──
          <>
            <div
              className="flex items-start gap-10 rounded-lg border border-border-subtle bg-surface-sunken p-12"
              {...testIdProp(`${PREFIX}-no-card`)}
            >
              <Icon icon={AlertTriangle} size={18} aria-hidden className="mt-1 text-warning-600" />
              <p className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
                {/* Where cards are added is this host's place (EP-MNT5(h)). */}
                {paths.words.approveNoCard}
              </p>
            </div>
            <div className="mt-4 flex items-center justify-end gap-8">
              <Dialog.Close render={<Button appearance="saas" tone="secondary" size="md" />}>
                Cancel
              </Dialog.Close>
              <Button
                appearance="saas"
                tone="primary"
                size="md"
                leadingIcon={<Icon icon={CreditCard} size={16} aria-hidden />}
                onClick={goToPaymentMethods}
                testId={`${PREFIX}-add-card`}
              >
                Add a card
              </Button>
            </div>
          </>
        ) : (
          <>
            {/* ── The match payment itself is the focal point (D-CAM-41). ── */}
            <ChargeSummary
              mark={summaryMark}
              amount={amountLabel}
              amountQualifier={summaryQualifier}
              recipient={summaryRecipient}
              recipientAction={charitiesToggle}
              cardTop={charitiesList}
              caption={summaryCaption}
              context={summaryContext}
              items={summaryList.items}
              itemsOverflow={summaryList.overflow}
              rows={[
                {
                  label: 'Donor of record',
                  value: companyName ?? 'Your company',
                  testId: `${PREFIX}-summary-donor`,
                },
              ]}
              testId={`${PREFIX}-summary`}
            />

            {/* ── "Cover processing fees" (invariant 8): under the amount, above "Pay with", off on
                every open; its line the server's fee. Locked while there is no price, while a charge
                is in flight or in doubt, when the company covers every charge, and when there is
                nothing to cover. ── */}
            <CoverFeesRow
              checked={
                pendingPricing !== null
                  ? pendingPricing.covered
                  : quoteStatus === 'failed'
                    ? false
                    : covered
              }
              onCheckedChange={(next) => {
                setCoverFees(next);
                // A new, un-charged attempt: a prior no-charge notice goes. The choice is part of
                // the series signature, so the next charge keys itself.
                if (phase.status === 'fatal-error') setPhase({ status: 'idle' });
              }}
              disabled={coverFeesLocked}
              title={rowCopy.title}
              line={rowCopy.line}
              lineIsReason={rowCopy.lineIsReason}
              testId={`${PREFIX}-cover-fees`}
            />

            <Select
              label="Pay with"
              options={fundingOptions}
              value={fundingValue}
              onValueChange={(v) => {
                setFundingValue(v);
                // A funding change (card↔card OR card↔wallet) is a new charge series — drop the key
                // and its series + clear any prior FATAL error. Disabled while `locked` (charging /
                // maybe-charged), so this never fires during a charge or a maybe-charged state. After
                // a maybe-charged CLOSE the dialog is idle and this control is live again, which is
                // why the open transition restores a re-entered approval's own source itself (the
                // admin never has to re-pick it here): choosing another source is a deliberate new
                // charge.
                idempotency.current.clear();
                series.current = null;
                if (phase.status === 'fatal-error') {
                  setPhase({ status: 'idle' });
                }
              }}
              disabled={locked}
              testId={`${PREFIX}-card-select`}
            />

            <Dialog.Description
              className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary"
              {...testIdProp(`${PREFIX}-charge-line`)}
            >
              {body}
            </Dialog.Description>

            {(phase.status === 'retryable-error' || phase.status === 'fatal-error') && (
              <div
                className="flex flex-col items-start gap-2"
                {...testIdProp(
                  phase.status === 'retryable-error'
                    ? `${PREFIX}-retryable-error`
                    : `${PREFIX}-fatal-error`,
                )}
              >
                <p className="font-sans text-s13 font-w500 leading-h15 tracking-givmo text-danger-700">
                  {phase.message}
                </p>
                {phase.reference !== null && (
                  <p className="font-sans text-s12 font-w400 tracking-normal text-text-tertiary">
                    {phase.reference}
                  </p>
                )}
              </div>
            )}

            {funding !== null &&
              shownDisclosureLegs === null &&
              disclosureState.status === 'unavailable' && (
                // ── The disclosure did not resolve (invariant 7): Confirm stays disarmed; say why. ──
                <DisclosureUnavailable
                  message={
                    target?.kind === 'all'
                      ? DISCLOSURE_UNAVAILABLE_ALL_BODY
                      : DISCLOSURE_UNAVAILABLE_BODY
                  }
                  onRetry={reloadDisclosure}
                  testId={`${PREFIX}-disclosure-unavailable`}
                />
              )}

            {/* ── The donor disclosure (invariant 7), directly above the confirm control: the bodies
                a held series was stated under, or the ones just read. ── */}
            {shownDisclosureLegs !== null && (
              <ChargeDisclosureTerms
                sections={matchDisclosureSections(shownDisclosureLegs)}
                testId={`${PREFIX}-disclosure`}
              />
            )}

            {phase.status === 'retryable-error' && closeAck ? (
              // ── Maybe-charged close acknowledgement (invariant 2): the footer is replaced so the
              //    ONLY ways forward are an explicit acknowledged close or going back to Try again. ──
              <div
                ref={closeAckRef}
                className="flex scroll-mb-24 flex-col gap-12 rounded-lg border border-border-subtle bg-surface-sunken p-12"
                {...testIdProp(`${PREFIX}-close-ack`)}
              >
                <p className="font-sans text-s13 font-w500 leading-h15 tracking-givmo text-text-secondary">
                  {CLOSE_ACK_BODY}
                </p>
                <div className="flex items-center justify-end gap-8">
                  <Button
                    appearance="saas"
                    tone="secondary"
                    size="md"
                    onClick={() => setCloseAck(false)}
                    testId={`${PREFIX}-close-ack-back`}
                  >
                    Go back
                  </Button>
                  <Button
                    appearance="saas"
                    tone="primary"
                    size="md"
                    onClick={finalizeClose}
                    testId={`${PREFIX}-close-ack-confirm`}
                  >
                    I understand — close
                  </Button>
                </div>
              </div>
            ) : (
              <div className="mt-4 flex items-center justify-end gap-8">
                <Dialog.Close
                  disabled={busy}
                  render={<Button appearance="saas" tone="secondary" size="md" />}
                >
                  Cancel
                </Dialog.Close>
                <Button
                  appearance="saas"
                  tone="primary"
                  size="md"
                  // Single-flight: disabled while a charge is in flight. A double-click cannot fire a
                  // second POST (this + the `inFlight` ref) and cannot mint a second key. Also disabled
                  // until a funding source is selected (`funding === null`) — never charge without one —
                  // and, for a single/grouped target, until the budget-capped amount has RESOLVED: the
                  // button's own label is that number, so confirming before it lands would be
                  // confirming the uncapped request the backend would reject. And until the donor
                  // disclosure for the selected rail has resolved (invariant 7), and the server has
                  // priced it (invariant 8), and while a selected wallet cannot cover the gross it
                  // states (CAM-R80). A held series resends what it was confirmed with, so it waits on
                  // none of these (invariant 1).
                  disabled={
                    busy ||
                    funding === null ||
                    walletShort ||
                    (pinned === null &&
                      (!amountResolved || disclosureLegs === null || quoteStatus !== 'ready'))
                  }
                  loading={
                    busy ||
                    (pinned === null &&
                      (resolving ||
                        (funding !== null && disclosureState.status === 'loading') ||
                        quoteStatus === 'loading'))
                  }
                  onClick={() => void runCharge()}
                  testId={`${PREFIX}-confirm`}
                >
                  {phase.status === 'retryable-error'
                    ? 'Try again'
                    : target?.kind === 'all'
                      ? groupCount > 0
                        ? `Approve all (up to ${formatApprovalUsd(amount)})`
                        : 'Approve all'
                      : pinned === null && resolved.status === 'exhausted'
                        ? 'Approve'
                        : `Approve (${formatApprovalUsd(shownAmount)})`}
                </Button>
              </div>
            )}
          </>
        )}
      </Dialog.Content>
    </Dialog.Root>
  );
}
